// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Menu,
  ArrowLeft,
  LogOut,
  HelpCircle,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import { NotificationButton } from '../NotificationButton';
import { PendingNotificationsBell } from '../PendingNotificationsBell';
import { ThemeToggle } from '../ThemeToggle';
import { forceAppRefresh } from '../../lib/forceRefresh';

/**
 * Sticky top header: mobile menu trigger, area badge, notifications,
 * tour button, user dropdown.
 */
export default function Topbar({
  user,
  isAdminUser,
  currentArea,
  onOpenSidebar,
  onStartTour,
  onOpenSignature,
  onChangeArea,
  onLogout,
}) {
  const AreaIcon = currentArea.icon;

  return (
    <header className="sticky top-0 z-30 h-[72px] bg-card/80 backdrop-blur-md border-b border-border flex items-center justify-between px-4 lg:px-8">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden hover:bg-accent rounded-xl"
        onClick={onOpenSidebar}
        data-testid="mobile-menu-button"
      >
        <Menu className="w-5 h-5" />
      </Button>

      <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg ${currentArea.bgColor} dark:bg-opacity-20`}>
        <AreaIcon className={`w-4 h-4 ${currentArea.color}`} />
        <span className={`text-sm font-medium ${currentArea.color}`}>{currentArea.shortName}</span>
      </div>

      <div className="flex-1" />

      <div id="tour-theme-toggle">
        <ThemeToggle />
      </div>

      <div id="tour-notifications">
        <NotificationButton />
      </div>

      <PendingNotificationsBell />

      <Button
        variant="ghost"
        size="icon"
        className="rounded-xl ml-1 hover:bg-accent"
        onClick={onStartTour}
        data-testid="help-tour-button"
        title="Tour de ajuda"
      >
        <HelpCircle className="w-5 h-5 text-muted-foreground" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl ml-2 hover:bg-accent"
            data-testid="user-menu-button"
            id="tour-user-menu"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-primary/80 to-primary rounded-xl flex items-center justify-center shadow-md">
              <span className="text-primary-foreground font-semibold text-xs">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-xl shadow-xl">
          <div className="px-4 py-3">
            <p className="text-sm font-semibold text-foreground">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          {isAdminUser && (
            <DropdownMenuItem
              onClick={onOpenSignature}
              className="py-2.5 cursor-pointer"
              data-testid="my-signature-button"
            >
              <PenLine className="w-4 h-4 mr-2" />
              Minha Rubrica
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onChangeArea} className="py-2.5 cursor-pointer">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Trocar Área
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={forceAppRefresh}
            data-testid="force-refresh-button"
            className="py-2.5 cursor-pointer"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Forçar Atualização
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onLogout}
            data-testid="logout-button"
            className="py-2.5 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
